<template>
    <div class="articles">
        <div class="article-search-bar-container">
            <input class="article-search-bar" type="text" v-model="searchQuery"
                placeholder="Search for help articles" />
            <img v-if="!searchActive" class="article-search-bar-icon" :src="searchIcon" alt="Search"/>
            <img 
              v-else 
              class="article-search-bar-icon" 
              :src="clearSearchIcon" 
              alt="Clear search"
              @click="clearSearchQuery"
            />
        </div>
        <div v-if="kbUiFlags.isFetchingResults" class="article-search-loading">
            <img :src="loadingGif" alt="Loading articles" class="article-search-loading-gif"/>
        </div>
        <div v-else-if="articles.length === 0 && !searchInProgress" class="article-search-no-articles-container">
            <img class="article-search-no-articles" :src="noArticlesFoundIcon" alt="No articles found"/>
            <div class="article-search-no-articles-text">No article found</div>
        </div>
        <div v-else class="article-search-results">
            <div class="article-search-count" v-if="searchQuery.length >= 3">
                {{ this.articlesCountText }}
            </div>
            <div class="article-container">
                <div 
                    class="article" 
                    v-for="(article, index) in articles" 
                    :key="article.id"
                    @click="() => { previewArticle(article) }"
                    @mouseover="checkForArticleTruncation(index)"
                >
                    <div 
                        class="article-title"
                        :ref="'articleTitle' + index"
                        v-tooltip="isTruncated[index] ? {
                            content: removeMarkTags(article.title),
                            placement: 'bottom',
                            arrowSelector: '.tooltip-arrow',
                            delay: 300,
                        } : null"
                    >
                        {{ removeMarkTags(article.title) }}
                    </div>
                    <img class="article-search-no-articles" src="../assets/images/preview-article.svg"
                        alt="Preview article" />
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import searchIcon from '../assets/images/search.svg';
import loadingGif from '../assets/images/loading-articles.gif';
import clearSearchIcon from '../assets/images/clear-search.svg';
import noArticlesFoundIcon from '../assets/images/no-articles-found.svg';
import { sendGainSightEvent, SURFACE_KB_IN_CHAT_GS_EVENTS } from '../../gainSightIntegration';
import { debounce } from '../helpers/utils';

export default {
    name: 'ArticlesListing',
    props: {
        pinnedArticles: Array
    },
    data() {
        return {
            searchQuery: "",
            loadingGif,
            searchIcon,
            clearSearchIcon,
            noArticlesFoundIcon,
            isTruncated: {},
            debounceTimer: null,
            searchInProgress: false,
            displayedArticles: [],
        }
    },
    computed: {
        ...mapGetters({
            webChannelConfig: 'appConfig/getWebChannelConfig',
            searchResults: 'kbDetails/getSearchResults',
            kbUiFlags: 'kbDetails/getUiFlags',
            selectedArticle: 'kbDetails/getSelectedArticleDetails',
            kbDetails: 'kbDetails/getKbDetails',
        }),
        articles() {
            return this.displayedArticles;
        },
        searchActive() {
            return !!this.searchQuery;
        },
        articlesCountText() {
            return `${this.articles.length} article${this.articles.length > 1 ? 's' : ''} found`;
        },
        redirectUrl() {
            return `${this.kbDetails.origin}/${this.selectedArticle.categoryTitle.toLowerCase().replace(/\s+/g, '-')}/${this.selectedArticle.slug}`;
        },
    },
    methods: {
        ...mapActions('kbDetails', ['searchKbArticles', 'clearSearchResults', 'fetchArticleDetails']),
        clearSearchQuery() {
            this.searchQuery = ""
        },
        removeMarkTags(text) {
            return text.replace(/<\/?mark>/g, '');
        },
        async previewArticle(article) {
            const { websiteToken } = this.webChannelConfig;
            await this.fetchArticleDetails({ websiteToken, articleId: article.id });
            if (!this.kbUiFlags.isFetchingResults) {
                const articleClickData = {
                    articleTitle: article.title,
                    articleUrl: this.redirectUrl,
                };
                sendGainSightEvent(SURFACE_KB_IN_CHAT_GS_EVENTS.WIDGET_ARTICLE_CLICKED, articleClickData);
                window.bus.$emit('switch-to-help-tab');
                this.$router.push({
                    name: 'ArticleRenderer',
                    params: { id: this.selectedArticle.articleId },
                    query: { article: JSON.stringify(this.selectedArticle) }
                });
            } else {
                console.error('selectedArticle is undefined');
            }
        },
        checkForArticleTruncation(index) {
            this.$nextTick(() => {
                const element = this.$refs[`articleTitle${index}`][0];
                if (element) {
                    this.$set(this.isTruncated, index, element.scrollWidth > element.clientWidth);
                }
            });
        },
        async performSearch(query) {
            const { websiteToken } = this.webChannelConfig;
            await this.searchKbArticles({ websiteToken, query });
            this.searchInProgress = false;
            this.displayedArticles = this.searchResults;
        },
        sendWidgetSearchUsedGsEvent(query) {
            const searchKeyword = query;
            sendGainSightEvent(SURFACE_KB_IN_CHAT_GS_EVENTS.WIDGET_ARTICLE_SEARCH_USED, {searchKeyword});
        },
    },
    watch: {
        searchQuery(query) {
            if (query.length >= 3) {
                this.searchInProgress = true;
                this.debouncedPerformSearch(query);
                this.debouncedCallWidgetSearchUsedEvent(query);
            } else {
                clearTimeout(this.debounceTimer);
                this.displayedArticles = this.pinnedArticles;
                this.searchInProgress = false;
                this.clearSearchResults();
            }
        }
    },
    mounted() {
        this.displayedArticles = this.pinnedArticles;
        this.debouncedPerformSearch = debounce(this.performSearch, 500);
        this.debouncedCallWidgetSearchUsedEvent = debounce(this.sendWidgetSearchUsedGsEvent, 2000);
    }
};
</script>
<style lang="scss" scoped>
@import '~widget/assets/scss/_articlesInChat.scss';
</style>
